import { ContextMenuConfiguration, IContextMenuHeader, IContextMenuOption } from '@/app-modules/tombola-context-menu';
import { ChatPrizeResponse, ChatPrizeStatus, RoomUserModel } from '@/components/chat/_domain';
import { User } from '@/app-modules/store/modules/user';
import { Lang } from 'vuejs-localization';
import { IEventBusService } from '@/app-modules/event-bus/event-bus-service';
import { IPersistedStore } from '@/app-modules/store/persisted-store';
import { container } from '@/app-modules/_ioc';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { ILocalisationService } from '@/app-modules/localisation-service';
import { IUserProfileService } from '@/components/chat/_domain/modules/user-profile-service';
import { ISinBanService } from '@/components/chat/_domain/modules/sin-ban-service';
import { IChatPrizeService } from '@/components/chat/_domain/modules/chat-prize-service';
import { IChatServerService } from '@/app-modules/server-service';
import { ISafeplayService } from '@/components/chat/_domain/modules/safeplay-service';
import { UserPreferences } from '@/app-modules/store/modules/user-preferences';
import { UserChatStatus } from '@/components/chat/_domain/modules/user-chat-status-service/user-chat-status-service-interface';
import { UserSettingsUpdatedEventName } from '@/components/user-settings/_domain/models';
import { ConfigurationPaths, IChatModsConfiguration, IConfigurationService } from '@/app-modules/config';
import { ITechnicalIssueService } from '@/components/chat/_domain/modules/technical-issue-service';
import { IUserNominationService } from '@/components/chat/_domain/modules/user-nomination-service';
import { ChatContextInteractionSource } from './chat-context-interaction-source';
import { ChatContextInteractionType } from './chat-context-interaction-type';
import { IUserChatStatusService } from '@/components/chat/_domain/modules/user-chat-status-service/user-chat-status-service-interface';
import _ from 'lodash';

export const CMBanSinPrizeEventName = 'cmBanSinPrizeEvent';

class ChatContextMenuModule {
  private readonly $lang: Lang;
  private readonly eventBusService: IEventBusService;
  private readonly store: IPersistedStore;
  private readonly configService: IConfigurationService;
  private readonly userChatStatusService: IUserChatStatusService;
  private username: string;
  private chatRoom: string;
  private user: RoomUserModel;
  private ChatPrizeStatus: ChatPrizeStatus = new ChatPrizeStatus();
  private isPlayerNotesLiveFeatureToggleEnabled: boolean = false;
  private isUsePlayerNotesBetaLiveFeatureToggleEnabled: boolean = false;
  private isTechnicalIssueReferralEnabled: boolean = false;
  private isUserNominationEnabled: boolean = false;
  private persistedStore = container.get(SERVICE_IDENTIFIERS.IPersistedStore) as IPersistedStore;
  private userPreferences: UserPreferences = this.persistedStore.Get(UserPreferences);
  private isModalsAsChatContextInteractionEnabled: boolean = false;
  private cmUser: User = null;
  private playerNotesBetaList: string[] = [];

  constructor(
    private readonly chatServerService: IChatServerService,
    private readonly localisationService: ILocalisationService,
    private readonly userProfileService: IUserProfileService,
    private readonly sinBanService: ISinBanService,
    private readonly chatPrizeService: IChatPrizeService,
    private readonly safeplayService: ISafeplayService,
    private readonly technicalIssueService: ITechnicalIssueService,
    private readonly userNominationService: IUserNominationService
  ) {
    this.$lang = this.localisationService.getLangInstance();
    this.eventBusService = container.get(SERVICE_IDENTIFIERS.IEventBusService);
    this.configService = container.get(SERVICE_IDENTIFIERS.IConfigurationService);
    this.store = container.get(SERVICE_IDENTIFIERS.IPersistedStore);
    this.userChatStatusService = container.get(SERVICE_IDENTIFIERS.IUserChatStatusService);
    this.getcmUserInStore();
  }

  public async buildUserContextMenuConfiguration(username?: string, chatRoom?: string, user?: RoomUserModel): Promise<ContextMenuConfiguration> {
    this.setupUser(username, chatRoom, user);
    await this.initialiseFeatureTogglesState();
    const menuHeader = this.getContextMenuHeader();
    const menuOptions = await this.getContextMenuOptions();
    return new ContextMenuConfiguration(menuHeader, menuOptions);
  }

  private setupUser(username?: string, chatRoom?: string, user?: RoomUserModel): void {
    this.username = user ? user.username : username;
    this.chatRoom = user ? user.chatRoom : chatRoom;
    this.user = user;
  }

  private async getcmUserInStore() {
    this.cmUser = this.store.Get(User);
  }

  private async initialiseFeatureTogglesState() {
    const config = await this.configService.getConfiguration();
    this.isPlayerNotesLiveFeatureToggleEnabled = config.getConfigurationByPath(ConfigurationPaths.featuretoggle.player_notes_islive) === 'true';
    this.isUsePlayerNotesBetaLiveFeatureToggleEnabled = config.getConfigurationByPath(ConfigurationPaths.featuretoggle.use_player_notes_beta_islive) === 'true';
    this.isTechnicalIssueReferralEnabled = config.getConfigurationByPath(ConfigurationPaths.featuretoggle.technical_issue_referral_islive) === 'true';
    this.isUserNominationEnabled = config.getConfigurationByPath(ConfigurationPaths.featuretoggle.user_nomination_islive) === 'true';
    this.isModalsAsChatContextInteractionEnabled = config.getConfigurationByPath(ConfigurationPaths.featuretoggle.chatcontext_modals_islive) === 'true';
    if (this.isUsePlayerNotesBetaLiveFeatureToggleEnabled){
      this.playerNotesBetaList = this.getPlayernotesBetaList(config);
    }
  }

  private getPlayernotesBetaList(config: IChatModsConfiguration): string[] {
    const list = config.getConfigurationByPath(ConfigurationPaths.users.playernotes_beta);
    if (list !== undefined){
      return list.split(',');
    }
    return [];
  }

  private async getContextMenuOptions(): Promise<IContextMenuOption[]> {
    const persistedStore = container.get(SERVICE_IDENTIFIERS.IPersistedStore) as IPersistedStore;
    const userPreferences = persistedStore.Get(UserPreferences);

    const menuOptions: IContextMenuOption[] = [];
    const userChatStatus = (this.user) ? this.user.userChatStatus : await this.getUserStatusFromUserName(this.username);
    if (this.user) {
      menuOptions.push(this.getUserProfileMenuOption());
      if (userChatStatus === UserChatStatus.StandardUser) {
        if (this.isTechnicalIssueReferralEnabled){
          menuOptions.push(this.getTechnicalIssueReferralMenuOption());
        }
        menuOptions.push(this.getAwardChatPrizeMenuOption(this.user.notEligibleForChatPrize));
      }
    }

    if (userChatStatus === UserChatStatus.StandardUser) {

      // if the user context is missing, add the pay button and allow engine to handle the eligible checks
      if (this.user === undefined) {
        menuOptions.push(this.getAwardChatPrizeMenuOption(false));
      }

      menuOptions.push(this.getSafeplayReferralMenuOption());
      if (userPreferences.country.toLowerCase() === 'it') menuOptions.push(this.getAwardPromoPrizeMenuOption());
      if (this.isUserNominationEnabled) menuOptions.push(this.getUserNominationMenuOption());
      if (this.shouldShowPlayerNotes()) {
        menuOptions.push(this.getPlayerNotesMenuOption());
     }
    }

    if (this.shouldShowSinBanUser(userChatStatus)){
      menuOptions.push(this.getSinMenuOption(), this.getBanMenuOption());
    }

    return _.sortBy(menuOptions, 'order');
  }

  private async getUserStatusFromUserName(username: string): Promise<UserChatStatus>{
    const userstatus = await this.userChatStatusService.getChatStatusForUser(username);
    return userstatus;
  }

  private shouldShowPlayerNotes(){
    if (!this.isPlayerNotesLiveFeatureToggleEnabled){
      return false;
    }

    if (this.isUsePlayerNotesBetaLiveFeatureToggleEnabled){
      return this.playerNotesBetaList.includes(this.cmUser.username);
    }

    return true;
  }

  private shouldShowSinBanUser(userChatStatus: UserChatStatus): boolean{
    // All CMs can ban Standard Users
    if (userChatStatus === UserChatStatus.StandardUser || userChatStatus === undefined){
      return true;
    }

    switch (this.cmUser.userChatStatus) {
      case UserChatStatus.ShiftLeader:
          if (userChatStatus === UserChatStatus.ChatModerator) {
            return true;
          } else {
            return false;
          }
      case UserChatStatus.TeamLeader:
          return true;
      default:
        return false; // a chatmod can only ban normal users so should be caught at the top
    }
  }

  private getContextMenuHeader(): IContextMenuHeader {
    return {
      headerText: this.username,
      iconCssClass: 'fa-user'
    };
  }

  private getUserProfileMenuOption(): IContextMenuOption {
    return {
      optionText: this.$lang.chat.contextMenu.profile,
      iconCssClass: 'fa-user',
      menuOptionSelectedCallback: () => this.userProfileService.viewUserProfile(this.user),
      order: 0,
    };
  }

  private getAwardChatPrizeMenuOption(isDisabled: boolean): IContextMenuOption {
    return {
      optionText: this.$lang.chat.contextMenu.pay,
      iconCssClass: 'fa-gbp',
      optionClass: 'chat-prize',
      menuOptionSelectedCallback: () =>  this.chatPrizeService.awardPlayerChatPrize([this.username], this.ChatPrizeStatus, this.chatServerService),
      isDisabled,
      order: 2,
    };
  }

  private getAwardPromoPrizeMenuOption(): IContextMenuOption {
    return {
      optionText: this.$lang.chat.contextMenu.promoPrize,
      iconCssClass: 'fa-gift',
      optionClass: 'chat-prize',
      menuOptionSelectedCallback: () => this.chatPrizeService.awardPlayerPromoPrize(this.username),
      order: 1,
    };
  }

  private getSinMenuOption(): IContextMenuOption {
    return {
      optionText: this.$lang.chat.contextMenu.sin,
      iconCssClass: 'fa-trash-o',
      optionClass: 'sin-option',
      menuOptionSelectedCallback: () => this.isModalsAsChatContextInteractionEnabled ? this.spawnChatContextInteractionModal(ChatContextInteractionType.SinPlayer, this.username, this.handleChatContextInteraction.bind(this), this.chatRoom)
      : this.sinBanService.sinBinUser(this.username, this.chatServerService, this.chatRoom),
      order: 3,
    };
  }

  private getBanMenuOption(): IContextMenuOption {
    return {
      optionText: this.$lang.chat.contextMenu.ban,
      iconCssClass: 'fa-ban',
      optionClass: 'ban-option',
      menuOptionSelectedCallback: () => this.isModalsAsChatContextInteractionEnabled ? this.spawnChatContextInteractionModal(ChatContextInteractionType.BanPlayer, this.username, this.handleChatContextInteraction.bind(this), this.chatRoom)
      : this.sinBanService.banUser(this.username, this.chatServerService, this.chatRoom),
      order: 4,
    };
  }

  private getSafeplayReferralMenuOption(): IContextMenuOption {
    return {
      optionText: this.$lang.chat.contextMenu.safeplayReferral,
      iconCssClass: 'fa-exclamation-triangle',
      optionClass: 'safeplay-option',
      menuOptionSelectedCallback: () => this.isModalsAsChatContextInteractionEnabled ? this.spawnChatContextInteractionModal(ChatContextInteractionType.SafePlayReferral, this.username, this.handleChatContextInteraction.bind(this))
      : this.safeplayService.referUser(this.username),
      order: 5,
    };
  }

  private getPlayerNotesMenuOption(): IContextMenuOption {
    return {
      optionText: this.$lang.chat.contextMenu.playerNotes,
      iconCssClass: 'fa-sticky-note',
      optionClass: 'notes-option',
      menuOptionSelectedCallback: () => this.spawnPlayerNotesModal(this.username),
      order: 6,
    };
  }

  private getTechnicalIssueReferralMenuOption(): IContextMenuOption {
    return {
      optionText: this.$lang.chat.contextMenu.technicalIssueReferral,
      iconCssClass: 'fa-bug',
      optionClass: 'technical-option',
      menuOptionSelectedCallback: () => this.isModalsAsChatContextInteractionEnabled ? this.spawnChatContextInteractionModal(ChatContextInteractionType.TechnicalReferral, this.username, this.handleChatContextInteraction.bind(this))
      : this.technicalIssueService.referUser(this.user),
      order: 7,
    };
  }

  private getUserNominationMenuOption(): IContextMenuOption {
    return {
      optionText: this.$lang.chat.contextMenu.userNomination,
      iconCssClass: 'fa-star',
      optionClass: 'nominate-option',
      menuOptionSelectedCallback: () => this.isModalsAsChatContextInteractionEnabled ? this.spawnChatContextInteractionModal(ChatContextInteractionType.CommunityNomination, this.username, this.handleChatContextInteraction.bind(this))
      : this.userNominationService.referUser(this.username),
      order: 8,
    };
  }

  public updateChatPrizeStatus(payload: ChatPrizeResponse) {
    if (this.ChatPrizeStatus.username === this.username) {
      this.ChatPrizeStatus.chatPrizeSuccess = payload.success;
      this.ChatPrizeStatus.responseMessage = payload.errorMessage;
      this.chatPrizeService.showChatPrizeSuccessOrFailureMessage(this.ChatPrizeStatus, this.username);
      this.ChatPrizeStatus = new ChatPrizeStatus();
    }
  }

  private spawnPlayerNotesModal(username: string): void {
    this.savePlayerNotesUsernameToStore(username);
    this.eventBusService.getEventBus().$emit('spawnPlayerNotesModal');
  }

  private spawnChatContextInteractionModal(chatContextAction: ChatContextInteractionType, playerUsername: string, handleChatContextInteractionCallback: (chatContextAction: ChatContextInteractionType, actionReason?: string, actionChatRoom?: string) => void, originalChatRoomName?: string): void {
    this.eventBusService.getEventBus().$emit('spawnChatContextInteractionModal', chatContextAction, playerUsername, handleChatContextInteractionCallback, originalChatRoomName, ChatContextInteractionSource.ChatContextMenu);
  }

  private savePlayerNotesUsernameToStore(username: string): void {
    const persistedStore = container.get(SERVICE_IDENTIFIERS.IPersistedStore) as IPersistedStore;
    const userPreferences = persistedStore.Get(UserPreferences);
    userPreferences.playerNotesUsername = username;
    persistedStore.Save(userPreferences);
    this.emitUserSettingsUpdatedEvent();
  }

  private emitUserSettingsUpdatedEvent(): void {
    this.eventBusService.getEventBus().$emit(UserSettingsUpdatedEventName);
  }

  private formatMessageForUser(message: string, username: string): string {
    return message.format({ username });
  }

  private handleChatContextInteraction(chatContextAction: ChatContextInteractionType, actionReason?: string, actionChatRoom?: string): void {
    switch (chatContextAction) {
      case ChatContextInteractionType.BanPlayer:
        this.sinBanService.banUser(this.username, this.chatServerService, (actionChatRoom) ? actionChatRoom : (this.user) ? this.user.chatRoom : this.chatRoom, actionReason);
        break;
      case ChatContextInteractionType.SinPlayer:
        this.sinBanService.sinBinUser(this.username, this.chatServerService, (actionChatRoom) ? actionChatRoom : (this.user) ? this.user.chatRoom : this.chatRoom, actionReason);
        break;
      case ChatContextInteractionType.SafePlayReferral:
        this.safeplayService.referUser(this.username, actionReason);
        break;
      case ChatContextInteractionType.TechnicalReferral:
        this.technicalIssueService.referUser(this.user, actionReason);
        break;
      case ChatContextInteractionType.CommunityNomination:
        this.userNominationService.referUser(this.username, actionReason);
      default:
        break;
    }
  }
}

export { ChatContextMenuModule };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat/_domain/modules/chat-context-menu/chat-context-menu.ts